import React from 'react';
import {useNavigate} from 'react-router-dom';
import {Stack, Typography, useTheme} from '@mui/material';

import {QueryParamsEnum} from 'presentation/constants';
import Icon from 'presentation/icons';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {translations} from 'presentation/translations-new';
import {formatText, getUfPath} from 'presentation/utils';
import {useQueryParamsSync} from 'presentation/pages/private/booking/hooks/useQueryParamsSync';
import {StyledButton} from './styles';
import {CurrentLocationProps} from './types';

const {yourCurrentLocation, change, locationSelected} = translations['pt-br'].commons;

export function CurrentLocation({flowType, dataTagId}: CurrentLocationProps) {
	const {bookingData, setSearchParams, setActiveBookingFlowStep} = useQueryParamsSync();
	const resetBookingData = useBookingStore(state => state.resetBookingData);
	const isUsingGeolocation = !bookingData?.isManualLocation;
	const navigate = useNavigate();
	const {cityName, ufCode} = bookingData;
	const {palette} = useTheme();
	const bookingUfPath = getUfPath(flowType);

	const {LOCATION_STEP} = getStepPosition({flowType, isManualLocation: true});

	const onClick = () => {
		resetBookingData();

		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);
				prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
				prev.delete(QueryParamsEnum.SHIFT);
				prev.delete(QueryParamsEnum.DISABILITY);
				prev.delete(QueryParamsEnum.INCLUDE_NEIGHBORHOOD);

				return prev;
			},
			{replace: true},
		);

		if (isUsingGeolocation) {
			navigate(bookingUfPath);
			return;
		}

		setActiveBookingFlowStep(LOCATION_STEP);
	};

	return (
		<Stack direction="row" justifyContent={'space-between'} gap={1.5}>
			<Stack direction="row" gap={1} alignItems={'center'}>
				<Icon name="location-on-filled" size={24} color={palette.primary.main} />
				<Stack>
					<Typography variant="caption" color="text.secondary">
						{isUsingGeolocation ? yourCurrentLocation : locationSelected}
					</Typography>
					<Typography variant="subtitle2" color="primary" fontWeight={600}>
						{formatText(cityName)}/{ufCode}
					</Typography>
				</Stack>
			</Stack>
			<StyledButton data-tag-id={dataTagId} variant="text" onClick={onClick}>
				{change}
			</StyledButton>
		</Stack>
	);
}
